"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChartLine, Megaphone, Handshake, Globe, House, ShieldCheck, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Local Expertise",
  description: "Our inspectors possess deep knowledge of South Florida's unique building codes and environmental challenges, from humidity to hurricane standards.",
  icon: Globe
}, {
  title: "Detailed Reporting",
  description: "Receive comprehensive, easy-to-read digital reports featuring high-resolution photos and clear explanations of every finding.",
  icon: ChartLine
}, {
  title: "Fast Turnaround",
  description: "We understand that time is of the essence in real estate. Expect your full inspection report delivered within 24 hours of the site visit.",
  icon: Megaphone
}, {
  title: "Licensed & Insured",
  description: "Rest easy knowing your property is in the hands of fully certified professionals who adhere to the highest industry standards of safety.",
  icon: ShieldCheck
}, {
  title: "Same-Day Scheduling",
  description: "Need an inspection fast? Our flexible team offers rapid scheduling options to keep your home buying or selling process moving forward.",
  icon: House
}, {
  title: "Professional Integrity",
  description: "We provide unbiased, honest assessments. Our goal is to empower you with the facts you need to make a confident property decision.",
  icon: Handshake
}];
export default function WhyChooseUsForSelling() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  const ctaReveal = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="249125" id="why-choose-trusted-inspections" className="w-full py-24 bg-background text-foreground overflow-hidden">
            <div className="container mx-auto px-4 md:px-6">
                <div ref={headerReveal.ref} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                    <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground">
                        The Trusted Advantage
                    </Badge>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight mb-6 text-foreground">
                        Why Choose Trusted Inspections?
                    </h2>
                    <p className="text-lg text-muted-foreground font-default">
                        We provide the clarity and detail necessary for informed property decisions, combining local South Florida insight with professional precision.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
                    {features.map((feature, index) => {
          const cardReveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          const Icon = feature.icon;
          return <div key={index} data-index={index} ref={cardReveal.ref} className={`transition-all duration-700 ease-out ${cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                                <Card className="h-full border-border bg-card hover:bg-accent/50 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-400 ease-out group">
                                    <CardHeader>
                                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-400">
                                            <Icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                                        </div>
                                        <CardTitle className="text-xl font-default text-foreground">{feature.title}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <CardDescription className="text-base text-muted-foreground font-default leading-relaxed">
                                            {feature.description}
                                        </CardDescription>
                                    </CardContent>
                                </Card>
                            </div>;
        })}
                </div>

                <div ref={ctaReveal.ref} className={`bg-muted rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto transition-all duration-1000 ease-out ${ctaReveal.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
                    <h3 className="text-2xl md:text-3xl font-bold font-default mb-4 text-foreground">
                        Ready to schedule your professional inspection?
                    </h3>
                    <p className="text-muted-foreground font-default mb-8 max-w-2xl mx-auto">
                        Get a detailed assessment of your property from our certified experts. We offer same-day scheduling and 24-hour report delivery.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button asChild size="lg" className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-400">
                            <Link to="/HomeInspectionServicePage">
                                Book Your Inspection
                                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-400" />
                            </Link>
                        </Button>
                        <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary text-primary hover:bg-primary/5">
                            <Link to="/HomePage">
                                View All Services
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>
        </section>;
}